import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  QueryKey,
} from '@tanstack/react-query';
import { useAuthStore } from '../store/auth';
import { useErrorHandler, ErrorResponse } from '../../hooks/use-error-handler';

// Helper to process API errors consistently
const processApiError = (err: any): ErrorResponse => {
  // Extract errors object from backend response (e.g., {"isSuccess":false,"errors":{"Password":"..."}})
  const backendErrors = err.error?.errors || err.response?.data?.errors;

  const errorInfo = {
    error: err.error?.error || err.response?.data?.error || 'UNKNOWN_ERROR',
    message: err.error?.message || err.response?.data?.message,
    details: backendErrors || err.error?.details || err.response?.data?.details,
  };

  const apiError: ErrorResponse = {
    error: errorInfo,
    error_description:
      err.error_description ||
      err.error?.message ||
      err.message ||
      err.response?.data?.error_description,
  };

  if (errorInfo.error === 'invalid_refresh_token') {
    apiError.error = {
      error: 'invalid_refresh_token',
      message: 'LOGGED_OUT_DUE_SESSION_EXPIRATION',
    };
    apiError.error_description = 'LOGGED_OUT_DUE_SESSION_EXPIRATION';
  }

  return apiError;
};

// Helper to handle session expiration. Clears auth state (flagging the session
// as expired so the login page can show a banner) and redirects to /login.
// No toast/overlay here — the login-page banner is the user-facing signal.
//
// handleAuthError (https.ts) is the single authority on whether a 401 is a
// genuine expiry: it sets the sessionExpired flag for authenticated sessions
// only. We guard on that flag here so guest 401s and bad-credentials login
// 401s (both unauthenticated) neither redirect nor show the banner.
const handleSessionExpiration = (navigate: (path: string) => void) => {
  if (useAuthStore.getState().sessionExpired) {
    navigate('/login');
  }
};

type ToastVariant = 'default' | 'destructive';

type ApiError = ErrorResponse;

/**
 * A wrapper around React Query's useQuery that adds global error handling
 */
const PUBLIC_ROUTES = ['/login', '/register', '/forgot-password'];

type GlobalQueryOptions<TQueryFnData, TError, TData, TQueryKey extends QueryKey> = Omit<
  UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  'onError'
> & {
  messageMap?: Record<string, string>;
  variant?: ToastVariant;
  duration?: number;
  onError?: (error: TError) => void;
};

export const useGlobalQuery = <
  TQueryFnData = unknown,
  TError = ErrorResponse,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: GlobalQueryOptions<TQueryFnData, TError, TData, TQueryKey>
) => {
  const { handleError } = useErrorHandler();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);

  const hasHandledError = useRef(false);

  const queryResult = useQuery<TQueryFnData, TError, TData, TQueryKey>({
    ...options,
    retry: false,
  });

  useEffect(() => {
    if (queryResult.error && !hasHandledError.current) {
      hasHandledError.current = true;
      const err = queryResult.error as any;
      const apiError = processApiError(err);

      if (apiError.error?.error === 'invalid_refresh_token') {
        if (!isPublicRoute) {
          handleSessionExpiration(navigate);
        }
      } else {
        handleError(apiError, {
          variant: 'destructive',
        });
      }

      // Call the original onError if it exists
      if (options.onError) {
        options.onError(queryResult.error);
      }
    }

    // Reset the flag when the error changes
    if (!queryResult.error) {
      hasHandledError.current = false;
    }
  }, [queryResult.error, isPublicRoute, handleError, navigate, options, t]);

  return queryResult;
};

/**
 * useGlobalMutation Hook
 *
 * An enhanced React Query useMutation hook that handles authentication errors globally
 * by automatically logging out users and redirecting to login when refresh tokens expire.
 *
 * Features:
 * - Wraps React Query's useMutation with authentication error handling
 * - Automatically redirects to login page on invalid refresh tokens
 * - Preserves original onError callback functionality
 * - Fully typed with generics for mutation data, errors, variables and context
 *
 * @template TData The type of data returned by the mutation function
 * @template TError The type of error returned by the mutation function (defaults to ApiError)
 * @template TVariables The type of variables passed to the mutation function
 * @template TContext The type of context returned by onMutate
 *
 * @param {UseMutationOptions<TData, TError, TVariables, TContext>} option - Standard React Query mutation options
 * @returns {UseMutationResult<TData, TError, TVariables, TContext>} The mutation result object from React Query
 *
 * @example
 * // Basic usage
 * const { mutate, isLoading } = useGlobalMutation({
 *   mutationFn: (userData) => clients.post('users', JSON.stringify(userData)),
 *   onSuccess: (data) => {
 *     console.log('User created:', data);
 *   }
 * });
 */

export const useGlobalMutation = <
  TData = unknown,
  TError = ApiError,
  TVariables = void,
  TContext = unknown,
>(
  option: UseMutationOptions<TData, TError, TVariables, TContext>
) => {
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();

  return useMutation({
    ...option,
    onError: (errorData, variables, onMutateResult, context) => {
      const err = errorData as any;
      const apiError = processApiError(err);

      if (apiError.error?.error === 'invalid_refresh_token') {
        handleSessionExpiration(navigate);
        return;
      }

      // Handle validation errors
      if (apiError.error?.error === 'validation_failed' && apiError.error?.details) {
        handleError(apiError, {
          variant: 'destructive',
        });
        return;
      }

      // Handle specific error messages
      if (apiError.error_description || apiError.error?.message) {
        handleError(apiError, {
          variant: 'destructive',
        });
        return;
      }

      // Default error handling
      handleError(apiError, {
        variant: 'destructive',
      });

      // Call the original onError if provided
      option.onError?.(errorData, variables, onMutateResult as any, context as any);
    },
  });
};
